import React, { useEffect, useRef, useState } from "react";
import { useSignalR } from "../../context/SignalRContext.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../../context/UserContext.js";
import { Formik, Form } from "formik";
import ApiCall from "../../Apicall/ApiCall.js";
import { buyerResumeState } from "../../utils/postLoginRedirect";
import Swal from "sweetalert2";
import PublicPage from "../../public/PublicPage";
import ChooseActionForm from "../../public/components/ds/ChooseActionForm";
import { DEAL_ENDED_EVENT } from "../../public/lib/dealSession";
import ActiveDealPanel from "../../public/components/ds/ActiveDealPanel";
import { useBankIdVerification } from "../../public/components/auth/BankIdVerification";

// Presentation follows the reference RoleEntryPanels/Overview. All session
// state, the transaction-create payload and post-choice routing remain owned
// here, unchanged.
const ChooseAction = () => {
  const { t, i18n } = useTranslation();
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  // A deal needs a BankID-verified account: an e-mail sign-up verifies here, then carries on.
  const { ensureVerified, verificationDialogs } = useBankIdVerification();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const response = await ApiCall({
          url: "/Session/GetCurrentLoginInformations",
          method: "GET",
        });
        const id = response.data?.result?.user?.id;
        setUserId(id);
      } catch (err) {
        console.error("Error fetching user ID:", err);
      }
    };

    fetchUserId();
  }, []);

  const { refreshUserInfo } = useUser();

  // The user's open deal (as seller, or as a connected buyer) is shown here instead of the
  // Buy/Sell entry, so they can carry on where they left off.
  const [activeDeal, setActiveDeal] = useState(null);
  // The composition follows the deal state: the ongoing-deal panel while the user has an open
  // deal, otherwise the standard Buy/Sell entry. There is no manual switch.
  // Bumped when a deal this user was in has ended (cancelled by the other party / expired), so the
  // page reloads its deal and falls back to the standard Buy/Sell entry.
  const [dealEndedTick, setDealEndedTick] = useState(0);
  useEffect(() => {
    const onEnded = () => setDealEndedTick((n) => n + 1);
    window.addEventListener(DEAL_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(DEAL_ENDED_EVENT, onEnded);
  }, []);
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const activeRes = await ApiCall({
          url: "/ContractMain/GetActiveContractByUserId",
          method: "GET",
          params: { Id: userId },
        });
        let active = activeRes?.result || activeRes?.data?.result;
        // Where a signed, unfinished deal stands ("payment" or "ownership"), when that is what this is.
        let awaitingStage = null;
        if (!active?.id || (active.sellerUserId !== userId && !active.buyerDealConnected)) {
          // Signing closes the deal, so one whose payment is still unconfirmed is no longer an
          // "active contract" — but it is still this user's ongoing deal and belongs in this
          // panel, so "View the deal" takes both parties back to its payment stage.
          active = null;
          try {
            const pendingRes = await ApiCall({ url: "/ContractPayment/GetDealAwaitingPayment", method: "GET" });
            const pending = pendingRes?.data?.result || pendingRes?.result;
            if (pending?.contractId) {
              active = { id: pending.contractId };
              awaitingStage = pending.stage || null;
            }
          } catch (_) {
            /* nothing pending */
          }
          if (!active) {
            if (!cancelled) setActiveDeal(null);
            return;
          }
        }
        let detail = active;
        try {
          const res = await ApiCall({ url: "/ContractMain/GetContractMainById", method: "GET", params: { Id: active.id } });
          detail = res?.result || res?.data?.result || active;
        } catch (_) {
          /* the summary still renders from the active contract */
        }
        // Without the full contract there is nothing to draw (the fallback above carries only an
        // id), so the standard Buy/Sell entry stays rather than a half-empty panel.
        if (!cancelled) setActiveDeal(detail?.sellerUserId ? { ...detail, awaitingStage, fetchedAt: Date.now() } : null);
      } catch (_) {
        /* no panel: the standard Buy/Sell entry stays */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, dealEndedTick]);

  // Both parties may have left the deal (the group is dropped when leaving), leaving them on this
  // page with the deal still open. Stay in its group while it is shown, so the other party's
  // sign-out (manual or idle) or cancellation reaches this page too, as it does on the deal pages.
  const { dealId: connectedDealId, connectToDeal, leaveDealGroup } = useSignalR();
  const joiningDeal = useRef(null);
  const joinedByPage = useRef(null);
  useEffect(() => {
    const id = activeDeal?.id;
    if (!id) {
      // the deal is over (cancelled / expired): drop the group this page joined for it
      if (joinedByPage.current && String(connectedDealId) === String(joinedByPage.current)) leaveDealGroup();
      joinedByPage.current = null;
      return;
    }
    if (String(connectedDealId) === String(id) || joiningDeal.current === id) return;
    joiningDeal.current = id;
    joinedByPage.current = id;
    connectToDeal(id)
      .catch(() => {
        /* the deal pages and the periodic notice check still cover this user */
      })
      .finally(() => {
        joiningDeal.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDeal?.id, connectedDealId]);

  const resumeDeal = () => {
    if (!activeDeal) return;
    localStorage.setItem("currentContractID", activeDeal.id);
    refreshUserInfo();
    // Both parties left a signed deal at the stage it stands at - its payment, or, once the money
    // is confirmed, its change of ownership - so that is where "View the deal" brings them back
    // to, not the signing flow they have already finished.
    if (activeDeal.dealComplete && !activeDeal.dealCancel) {
      navigate(activeDeal.awaitingStage === "ownership" ? `/Ownership/${activeDeal.id}` : `/Payment/${activeDeal.id}`);
      return;
    }
    if (activeDeal.sellerUserId === userId) navigate("/SellerProfile");
    else navigate("/BuyerProfile", { state: buyerResumeState(activeDeal) });
  };

  return (
    <PublicPage>
      <Formik
        enableReinitialize
        initialValues={{
          sellerUserId: userId,
          sellerDealStatus: "",
          sellerDealActive: true,
          sellerDealComplete: false,
          vahicleTypeOptionId: null,
          vahicleTypeOptionName: "",
          sellerDrivingLicensePath: "",
          sellerDrivingLicenseValidate: true,
          carInfoId: null,
          carValuationBySeller: 0,
        }}
        onSubmit={async () => {
          setLoading(true);
          try {
            // NO contract is created here. A contract is created only after the
            // seller has entered a registration number AND the vehicle registry
            // (Bilvision) lookup has succeeded (SellerProfileFlow.handleSearch).
            // If the user already has an open deal, resume it instead of
            // starting a new one.
            if (userId) {
              const activeRes = await ApiCall({
                url: "/ContractMain/GetActiveContractByUserId",
                method: "GET",
                params: { Id: userId },
              });
              const active = activeRes?.result || activeRes?.data?.result;
              if (active?.id) {
                localStorage.setItem("currentContractID", active.id);
                refreshUserInfo();
                if (active.sellerUserId === userId) navigate("/SellerProfile");
                else if (active.buyerDealConnected) navigate("/BuyerProfile", { state: buyerResumeState(active) });
                else navigate("/BuyerSuccess");
                return;
              }
            }
            localStorage.removeItem("currentContractID");
            refreshUserInfo();
            navigate("/SellerProfile");
          } catch (error) {
            // ABP wraps a thrown UserFriendlyException (e.g. the
            // "you already have an active deal" guard) as a 500 response
            // whose body still carries the real, friendly message/details —
            // error.message here is just axios's generic
            // "Request failed with status code 500" unless this is read
            // from the response body instead, same as SellerProfileFlow.jsx
            // already does for its own ContractMain calls.
            const backendError = error?.response?.data?.error;
            const message =
              [backendError?.message, backendError?.details].filter(Boolean).join(": ") ||
              error?.message ||
              "Something went wrong!";
            Swal.fire("Error", message, "error");
          } finally {
            setLoading(false);
          }
        }}
      >
        {({ setFieldValue, submitForm }) => (
          <Form>
            <ChooseActionForm
              t={t}
              loading={loading}
              activeDealPanel={
                activeDeal ? (
                  <ActiveDealPanel t={t} language={i18n.language} deal={activeDeal} userId={userId} onResume={resumeDeal} onViewVehicle={() => navigate("/fordon")} />
                ) : null
              }
              onStartSell={async () => {
                if (!activeDeal && !(await ensureVerified())) return;
                setFieldValue("sellerDealStatus", "Seller");
                submitForm();
              }}
              onStartBuy={async () => {
                // A user with an open deal (as seller, or as a connected buyer) may not join another.
                try {
                  if (userId) {
                    const activeRes = await ApiCall({
                      url: "/ContractMain/GetActiveContractByUserId",
                      method: "GET",
                      params: { Id: userId },
                    });
                    const active = activeRes?.result || activeRes?.data?.result;
                    if (active?.id && (active.sellerUserId === userId || active.buyerDealConnected)) {
                      const res = await Swal.fire({
                        icon: "warning",
                        title: "Active deal in progress",
                        text: "You already have an active deal, so you cannot join another one. Complete or cancel it, or wait until it expires, first.",
                        showCancelButton: true,
                        confirmButtonText: "Go to my deal",
                        cancelButtonText: "Close",
                      });
                      if (res.isConfirmed) {
                        localStorage.setItem("currentContractID", active.id);
                        refreshUserInfo();
                        if (active.sellerUserId === userId) navigate("/SellerProfile");
                        else navigate("/BuyerProfile", { state: buyerResumeState(active) });
                      }
                      return;
                    }
                  }
                } catch (err) {
                  console.error("Active deal check failed:", err);
                }
                if (!(await ensureVerified())) return;
                navigate("/BuyerProfile");
              }}
            />
          </Form>
        )}
      </Formik>
      {verificationDialogs}
    </PublicPage>
  );
};

export default ChooseAction;
